import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axiosInstance from "../../api/axiosInstance";
import { useSocket } from "../../context/SocketContext";
import { loadStripe } from "@stripe/stripe-js";
import { useSearchParams } from 'react-router-dom';

export default function BusinessPostForm({
  isBusinessVerified,
  stripePublicKey
}) {
  const dbg = (...args) => {
    try { console.log('[BusinessPostForm]', ...args); } catch (_) {}
  };
  // Use provided prop or fall back to env (CRA exposes REACT_APP_* only)
  const effectiveStripeKey =
    stripePublicKey ||
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    process.env.STRIPE_PUBLISHABLE_KEY ||
    (typeof window !== 'undefined' ? window.STRIPE_PUBLISHABLE_KEY : undefined);
  console.log('BusinessPostForm: stripePublicKey prop=', stripePublicKey, ' effective=', effectiveStripeKey);
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business_id');
  const [cfg, setCfg] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [minAge, setMinAge] = useState(12);
  const [maxAge, setMaxAge] = useState(65);
  const [mediaFiles, setMediaFiles] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { filename: 0..100 }
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef();
  const [reservationCTA, setReservationCTA] = useState(false);
  const [purchaseCTA, setPurchaseCTA] = useState(false);
  const [reservationServiceId, setReservationServiceId] = useState("");
  const [purchaseProductId, setPurchaseProductId] = useState("");
  const [boostEnabled, setBoostEnabled] = useState(false);
  const boostCurrency = "EUR";
  const MIN_DAILY_DISPLAY = 5; // minimum allowed budget per day in display currency
  const [boostBudget, setBoostBudget] = useState(10); // display currency units
  const [boostDuration, setBoostDuration] = useState(3); // days
  const [countryCode, setCountryCode] = useState("");
  const [cityId, setCityId] = useState("");
  const [availableFrom, setAvailableFrom] = useState(() => {
    // default to now, or maybe tomorrow morning
    return new Date().toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
  });
  const clampAges = useCallback((minV, maxV) => {
    const minClamped = Math.max(12, Math.min(Number(minV || 12), 64));
    const maxClamped = Math.max(minClamped, Math.min(Number(maxV || 65), 65));
    return [minClamped, maxClamped];
  }, []);

  const apiAges = useMemo(() => {
    const [mn, mx] = clampAges(minAge, maxAge);
    // full-range => Any
    if (mn === 12 && mx === 65) return { min: null, max: null };
    return { min: mn, max: mx === 65 ? null : mx };
  }, [minAge, maxAge, clampAges]);
  const [estimating, setEstimating] = useState(false);
  const [est, setEst] = useState({
    p10: { impressions: 0, clicks: 0, conv: 0 },
    p50: { impressions: 0, clicks: 0, conv: 0 },
    p90: { impressions: 0, clicks: 0, conv: 0 }
  });
  const [budgetCaps, setBudgetCaps] = useState({ per_day: null, total: null });
  const [liveCapNote, setLiveCapNote] = useState("");
  const { socket } = useSocket?.() || { socket: null };
  const [sexTarget, setSexTarget] = useState('any');
  // Targeting presets state
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState("");
  const [newPresetName, setNewPresetName] = useState("");
  // no separate save action anymore; saving happens on post
  // saving happens only on publish
  const [deletingPreset, setDeletingPreset] = useState(false);

  function detectApplePlatform() {
    if (typeof navigator === "undefined") return { isIOS: false, isSafari: false, isPWA: false };
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";
    const vendor = navigator.vendor || "";
    const maxTouch = navigator.maxTouchPoints || 0;

    const isiPadOS13 = platform === "MacIntel" && maxTouch > 1;
    const isIOSUA =
      /iPhone|iPad|iPod/i.test(ua) ||
      /iPhone|iPad|iPod/i.test(platform) ||
      isiPadOS13;

    const isWindows = /Windows NT/i.test(ua);
    const isIOS = isIOSUA && !isWindows;

    const isSafari =
      /Safari/i.test(ua) &&
      !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua) &&
      /Apple/i.test(vendor);

    const isPWA =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;

    return { isIOS, isSafari, isPWA };
  }

  function isCapacitorNative() {
    if (typeof window === "undefined") return false;
    // v5+: isNativePlatform(); older: isNative boolean
    try {
      const C = window.Capacitor;
      return !!(C?.isNativePlatform?.() || C?.isNative);
    } catch {
      return false;
    }
  }
  const { isIOS } = React.useMemo(() => detectApplePlatform(), []);
  const isIOSNativeApp = React.useMemo(() => isIOS && isCapacitorNative(), [isIOS]);
  useEffect(() => {
    if (isIOSNativeApp && boostEnabled) setBoostEnabled(false);
  }, [isIOSNativeApp, boostEnabled]);

  // ───────────────────────── HELPERS
  const toBaseCents = useCallback(
    (amountDisplay, currency) => {
      if (!cfg) return 0;
      const rate = cfg.fx_table?.[currency] ?? 1.0; // display -> base
      const base = Math.round(Number(amountDisplay) * 100 * rate);
      return Number.isFinite(base) ? base : 0;
    },
    [cfg]
  );
  const fromBaseCentsToDisplay = useCallback((cents) => {
    if (!cfg) return 0;
    const rate = cfg.fx_table?.[boostCurrency] ?? 1.0; // display -> base
    // display = base / rate
    return Math.floor((cents / rate) / 100); // integer whole units for slider
  }, [cfg, boostCurrency]);

  const budgetMaxDisplay = useMemo(() => {
    if (!cfg) return 1000; // initial fallback until cfg arrives

    // Per-day caps (in base cents)
    const deliverablePerDay = Number.isFinite(budgetCaps?.per_day) ? budgetCaps.per_day : Infinity;
    const accountPerDay     = Number.isFinite(cfg?.caps?.remaining_daily_budget_cents)
      ? cfg.caps.remaining_daily_budget_cents
      : Infinity;
    // Per-campaign limit expressed as per-day cap (total / days)
    const perCampaignPerDay = Number.isFinite(cfg?.caps?.max_total_budget_cents_per_campaign)
      ? Math.floor(cfg.caps.max_total_budget_cents_per_campaign / Math.max(1, boostDuration))
      : Infinity;

    const perDayBaseCap = Math.min(deliverablePerDay, accountPerDay, perCampaignPerDay);
    const fallback = Math.min(accountPerDay, perCampaignPerDay);
    const chosenBaseCap = Number.isFinite(perDayBaseCap) ? perDayBaseCap : fallback;

    const maxDisplay = Math.max(5, fromBaseCentsToDisplay(chosenBaseCap));
    try {
      console.log('[BusinessPostForm][budget caps]', {
        deliverablePerDay,
        accountPerDay,
        perCampaignPerDay,
        perDayBaseCap,
        chosenBaseCap,
        maxDisplay,
        budgetCaps,
      });
    } catch (_) {}
    return maxDisplay;
  }, [cfg, budgetCaps?.per_day, boostDuration, fromBaseCentsToDisplay]);

  // If the available deliverable per day is below the minimum allowed daily budget, we cannot run an ad
  const belowMinDaily = useMemo(() => {
    if (!cfg) return false;
    if (budgetCaps?.per_day == null) return false;
    const minBase = toBaseCents(MIN_DAILY_DISPLAY, boostCurrency);
    return Number.isFinite(budgetCaps.per_day) && budgetCaps.per_day > 0 && budgetCaps.per_day < minBase;
  }, [cfg, budgetCaps?.per_day, boostCurrency, toBaseCents]);

  useEffect(() => {
    if (!boostEnabled) return;
    if (boostBudget > budgetMaxDisplay) setBoostBudget(budgetMaxDisplay);
  }, [boostEnabled, budgetMaxDisplay]);

  const countryObj = useMemo(
    () => cfg?.eligible_countries?.find((c) => c.code === countryCode),
    [cfg, countryCode]
  );

  const citiesForCountry = countryObj?.cities || [];
  const [eventCTA, setEventCTA] = useState(false);
  const [eventId, setEventId] = useState("");
  const [placements, setPlacements] = useState({
    home_feed: true,
    flashes: true,
    events_feed: true,
  });
  const ctaType = useMemo(() => {
    if (reservationCTA) return "reservation";
    if (purchaseCTA) return "product";
    if (eventCTA) return "event";
    return "none";
  }, [reservationCTA, purchaseCTA, eventCTA]);

  const objective = useMemo(() => {
    if (reservationCTA) return "reservations";
    if (purchaseCTA) return "purchases";
    if (eventCTA) return "event_views";
    return "profile_visits";
  }, [reservationCTA, purchaseCTA, eventCTA]);

  const boostGloballyAvailable = cfg ? !!cfg.boost_available : null;

  useEffect(() => {
    dbg('mount/useEffect: businessId =', businessId);
    if (!businessId) { setLoadingConfig(false); return; }
    let mounted = true;
    (async () => {
      setLoadingConfig(true);
      setFormError("");
      try {
        dbg('fetching /ad/post-form-config ...');
        const { data } = await axiosInstance.get("/ad/post-form-config", {
          params: { business_id: businessId }
        });
        if (!mounted) return;
        dbg('/ad/post-form-config OK', data);
        setCfg(data || null);
        setPresets(Array.isArray(data?.targeting_presets) ? data.targeting_presets : []);
      } catch (e) {
        if (!mounted) return;
        dbg('/ad/post-form-config ERROR', e?.response?.status, e?.response?.data || e?.message);
        setFormError("Failed to load configuration for ads.");
      } finally {
        if (mounted) setLoadingConfig(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [businessId]);

  // ───────────────────────── FILE HANDLERS
  const onDragOver = (e) => e.preventDefault();
  const handleDropped = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };
  const handleFiles = useCallback(
    (files) => {
      const current = [...mediaFiles];
      for (const file of files) {
        if (current.length >= 3) break;
        current.push(file);
      }
      validateMedia(current);
    },
    [mediaFiles]
  );

  const validateMedia = (files) => {
    const newErrors = { ...errors };
    if (files.length > 3) newErrors.media = "Max 3 files total.";
    else delete newErrors.media;

    const ALLOWED = new Set([
      "image/jpeg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/quicktime"
    ]);
    if ([...files].some((f) => !ALLOWED.has(f.type)))
      newErrors.media = "Only JPG, PNG, WEBP, MP4, MOV.";
    if ([...files].some((f) => f.size > 15 * 1024 * 1024))
      newErrors.media = "Each file must be ≤ 15MB.";

    // Allow videos even when CTAs are enabled

    setErrors(newErrors);
    if (!newErrors.media) setMediaFiles(files.slice(0, 3));
  };

  const removeMedia = (idx) =>
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));

  // ───────────────────────── PRESETS HELPERS
  const extractBasicTargeting = useCallback((payload) => {
    try {
      const p = payload || {};
      const t = p.targeting || p;
      const cc = Array.isArray(t.country_codes) ? t.country_codes[0] : (t.country_code || t.country);
      const city = Array.isArray(t.city_ids) ? t.city_ids[0] : (t.city_id || t.city);
      const mn = t.min_age ?? t.minAge ?? null;
      const mx = t.max_age ?? t.maxAge ?? null;
      const sx = (t.sex || t.gender || 'any').toLowerCase();
      return {
        countryCode: cc ? String(cc) : "",
        cityId: (city ?? "") === null ? "" : String(city ?? ""),
        minAge: typeof mn === 'number' ? mn : (mn == null ? 12 : Number(mn)),
        maxAge: typeof mx === 'number' ? (mx ?? 65) : (mx == null ? 65 : Number(mx)),
        sex: ['any','male','female','other'].includes(sx) ? sx : 'any',
      };
    } catch {
      return { countryCode: "", cityId: "", minAge: 12, maxAge: 65, sex: 'any' };
    }
  }, []);

  const refreshPresets = useCallback(async () => {
    if (!businessId) return;
    try {
      const { data } = await axiosInstance.get('/ad/targeting-presets', { params: { business_id: businessId } });
      setPresets(Array.isArray(data?.items) ? data.items : []);
    } catch (_) {
      // ignore
    }
  }, [businessId]);

  const applySelectedPreset = useCallback(() => {
    const p = presets.find(x => String(x.id) === String(selectedPresetId));
    if (!p) return;
    const basic = extractBasicTargeting(p.payload);
    setBoostEnabled(true);
    if (basic.countryCode) setCountryCode(basic.countryCode);
    setCityId(basic.cityId || "");
    const [mn, mx] = clampAges(basic.minAge, basic.maxAge);
    setMinAge(mn);
    setMaxAge(mx);
    setSexTarget(basic.sex || 'any');
  }, [presets, selectedPresetId, extractBasicTargeting, clampAges]);

  // Stable stringify for duplicate detection (order-independent)
  const stableStringify = (obj) => {
    const sortKeys = (o) => {
      if (Array.isArray(o)) return o.map(sortKeys);
      if (o && typeof o === 'object') {
        return Object.keys(o).sort().reduce((acc, k) => { acc[k] = sortKeys(o[k]); return acc; }, {});
      }
      return o;
    };
    try { return JSON.stringify(sortKeys(obj)); } catch { return ''; }
  };

  const deleteSelectedPreset = useCallback(async () => {
    const id = selectedPresetId;
    if (!id) return;
    setDeletingPreset(true);
    try {
      await axiosInstance.delete(`/ad/targeting-presets/${id}`, { params: { business_id: businessId } });
      setSelectedPresetId("");
      await refreshPresets();
    } catch (err) {
      setErrors((e) => ({ ...e, preset: err?.response?.data?.message || 'Failed to delete preset' }));
    } finally {
      setDeletingPreset(false);
    }
  }, [selectedPresetId, businessId, refreshPresets]);

  // ───────────────────────── ESTIMATION (conditional)
  useEffect(() => {
    if (!cfg) return;

    // Only estimate when: boosting and country chosen
    if (!boostEnabled || !countryCode) {
      setEst({
        p10: { impressions: 0, clicks: 0, conv: 0 },
        p50: { impressions: 0, clicks: 0, conv: 0 },
        p90: { impressions: 0, clicks: 0, conv: 0 }
      });
      return;
    }
    if (countryObj?.inventory === "none") return;

    let timer = null;
    const run = async () => {
      setEstimating(true);
      try {
        const hasEntity =
          (reservationCTA && !!reservationServiceId) ||
          (purchaseCTA && !!purchaseProductId);

        const payload = {
          objective,
          ctaType,
          targeting: {
            country_codes: [countryCode],
            city_ids: cityId ? [Number(cityId)] : null,
            min_age: minAge || null,
            max_age: maxAge || null,
            sex: sexTarget
          },
          durationDays: boostDuration,
          budgetDisplay: { amount: boostBudget, currency: boostCurrency },
          hasEntity,
        };

        const { data } = await axiosInstance.post("/ad/estimate", {
          objective,
          ctaType,
          targeting: {
            country_codes: [countryCode],
            city_ids: cityId ? [Number(cityId)] : null,
            min_age: apiAges.min,
            max_age: apiAges.max,
            sex: sexTarget
          },
          durationDays: boostDuration,
          budgetDisplay: { amount: boostBudget, currency: boostCurrency },
          start_at: availableFrom,
          hasEntity,
        });
        if (!data) return;
        console.log('[ad/estimate result]', {
          debug: data?.debug,
          budget_cap_cents: data?.budget_cap_cents,
          p50: data?.p50,
        });
        setEst({
          p10: data.p10 || { impressions: 0, clicks: 0, conv: 0 },
          p50: data.p50 || { impressions: 0, clicks: 0, conv: 0 },
          p90: data.p90 || { impressions: 0, clicks: 0, conv: 0 }
        });
        setBudgetCaps(data.budget_cap_cents || { per_day: null, total: null });
      } catch {
        // leave estimates as-is
      } finally {
        setEstimating(false);
      }
    };

    // debounce
    timer = setTimeout(run, 300);
    return () => clearTimeout(timer);
  }, [
    cfg,
    boostEnabled,
    ctaType,
    objective,
    countryCode,
    cityId,
    boostDuration,
    boostBudget,
    boostCurrency,
    reservationCTA,
    purchaseCTA,
    reservationServiceId,
    purchaseProductId,
    apiAges.min, apiAges.max, sexTarget
  ]);

  // WebSocket live cap subscription
  useEffect(() => {
    if (!socket || !cfg || !boostEnabled || !countryCode) return;
    if (businessId) socket.emit('join.business', businessId);
    const payload = {
      businessId,
      country: countryCode,
      cityId: cityId ? Number(cityId) : null,
      min_age: apiAges.min,
      max_age: apiAges.max,
      sex: sexTarget,
      durationDays: boostDuration,
      start_at: availableFrom
    };
    const onUpdate = ({ caps }) => {
      if (!caps) return;
      setBudgetCaps(caps);
      const capDisplay = fromBaseCentsToDisplay(caps.per_day);
      if (boostBudget > capDisplay) {
        setBoostBudget(capDisplay);
        setLiveCapNote(`Max available decreased to ${capDisplay} ${boostCurrency}/day due to updated availability.`);
        setTimeout(() => setLiveCapNote(""), 4000);
      }
    };
    const onRefresh = () => {
      try { socket.emit('ad.cap.subscribe', payload); } catch {}
    };
    socket.on('ad.cap.update', onUpdate);
    socket.on('ad.cap.refresh', onRefresh);
    socket.emit('ad.cap.subscribe', payload);
    return () => {
      try { socket.emit('ad.cap.unsubscribe'); } catch {}
      socket.off('ad.cap.update', onUpdate);
      socket.off('ad.cap.refresh', onRefresh);
    };
  }, [socket, cfg, boostEnabled, countryCode, cityId, apiAges.min, apiAges.max, sexTarget, boostDuration, availableFrom, businessId, fromBaseCentsToDisplay, boostBudget, boostCurrency]);
  useEffect(() => {
    if (!countryCode) return;
    // If this country exposes city targeting and none selected, don't warn yet.
    if ((citiesForCountry?.length || 0) > 0 && !cityId) {
      setErrors((e) => {
        const { ageTarget, ...rest } = e;
        return rest;
      });
      return;
    }
    const run = async () => {
      try {
        const { data } = await axiosInstance.post("/ad/check-audience", {
          country_code: countryCode,
          city_id: cityId || null,
          min_age: apiAges.min,
          max_age: apiAges.max,
          sex: sexTarget
        });
        if (!data.available) {
          console.log('[ad/check-audience] not available for', { country_code: countryCode, city_id: cityId || null, min_age: apiAges.min, max_age: apiAges.max, sex: sexTarget });
          setErrors((e) => ({
            ...e,
            ageTarget: "Selected age range has insufficient inventory."
          }));
        } else {
          setErrors((e) => {
            const { ageTarget, ...rest } = e;
            return rest;
          });
        }
      } catch {
        // optional: handle error
      }
    };
    run();
  }, [countryCode, cityId, apiAges.min, apiAges.max, sexTarget, citiesForCountry?.length]);

  // ───────────────────────── VALIDATION BEFORE SUBMIT
  const validateBeforeSubmit = () => {
    const newErrors = {};

    // basic
    if (mediaFiles.length === 0) newErrors.media = "Add at least one file.";
    // Allow videos with CTAs
    if (!caption.trim()) newErrors.caption = "Caption required.";

    // CTA mutual exclusivity
    if (reservationCTA && purchaseCTA) {
      newErrors.cta = "Choose Reservation OR Purchase, not both.";
    }
    if (eventCTA && (reservationCTA || purchaseCTA)) {
      newErrors.cta = "Choose only one CTA.";
    }
    // Boost validations
    if (boostEnabled) {
      // If targetable but inventory is too low to meet minimum daily spend, block submission
      if (belowMinDaily) {
        newErrors.budget = `Unfortunately you cannot place an ad with these targeting settings. Available inventory is below the minimum ${MIN_DAILY_DISPLAY} ${boostCurrency}/day.`;
      }
      if (!countryCode) newErrors.countryCode = "Choose a country.";
      if (countryObj?.inventory === "none") {
        newErrors.countryCode = "No inventory for this country yet.";
      }
      // caps: budget checks in base currency (boostBudget is PER-DAY)
      const budgetBase = toBaseCents(boostBudget, boostCurrency);
      const perDay = budgetBase;
      if (budgetCaps?.per_day != null && Number.isFinite(budgetCaps.per_day) && perDay > budgetCaps.per_day) {
        newErrors.budget = `Budget exceeds deliverable capacity for this audience. Max ~ ${(budgetCaps.per_day/100).toFixed(2)} ${cfg.base_currency}/day.`;
      }
      const maxPerCampaign = cfg?.caps?.max_total_budget_cents_per_campaign ?? Infinity;
      const remainingDaily = cfg?.caps?.remaining_daily_budget_cents ?? Infinity;

      if (budgetBase * Math.max(1, boostDuration) > maxPerCampaign) {
        newErrors.budget = `Budget exceeds per-campaign limit (${(maxPerCampaign / 100).toFixed(2)} ${cfg.base_currency}).`;
      }
      if (Number.isFinite(remainingDaily) && perDay > remainingDaily) {
        newErrors.budget = `Budget exceeds your remaining daily allowance (${(remainingDaily / 100).toFixed(2)} ${cfg.base_currency}/day).`;
      }

      // concurrent slots: only warn, do not block (backend checks overlap with selected dates)
      // if ((cfg?.caps?.remaining_concurrent_campaign_slots ?? 1) <= 0) {
      //   newErrors.caps = "You have no available campaign slots right now.";
      // }

    }

    setErrors(newErrors);
    const ok = Object.keys(newErrors).length === 0;
    try {
      console.log('[BusinessPostForm][validateBeforeSubmit]', ok ? 'OK' : 'INVALID', newErrors, {
        boostEnabled,
        boostBudget,
        budgetCaps,
        currency: boostCurrency,
        perDayBase: toBaseCents(boostBudget, boostCurrency),
      });
    } catch (_) {}
    if (!ok) {
      const first = newErrors.media || newErrors.caption || newErrors.cta || newErrors.countryCode || newErrors.budget || newErrors.caps || newErrors.ageTarget || 'Please review the highlighted fields.';
      setFormError(first);
    } else {
      setFormError("");
    }
    return ok;
  };

  // ───────────────────────── UPLOAD TO GCS (SIGNED URL)
  const uploadAllMedia = async () => {
    dbg('uploadAllMedia: begin, files=', mediaFiles?.length || 0);
    // Request a signed URL per file (batch)
    const signReq = {
      files: mediaFiles.map((f) => ({
        filename: f.name,
        contentType: f.type,
        size: f.size
      }))
    };

    const { data: signed } = await axiosInstance.post("/ad/uploads/gcs/sign", signReq);
    dbg('signed URLs response:', signed);
    const items = signed?.items || [];
    if (items.length !== mediaFiles.length) {
      throw new Error("Could not prepare uploads.");
    }

    setUploading(true);
    setUploadProgress({});
    const uploaded = [];
    try {
      for (let i = 0; i < mediaFiles.length; i++) {
        const file = mediaFiles[i];
        const meta = items[i];
        if (!meta?.uploadUrl || !meta?.publicUrl) {
          throw new Error("Missing upload metadata.");
        }
        await new Promise((resolve, reject) => {
          dbg('upload start', { index: i, filename: file?.name, type: file?.type, to: meta.uploadUrl?.slice?.(0, 80) + '...' });
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", meta.uploadUrl);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.upload.onprogress = (evt) => {
            if (!evt.lengthComputable) return;
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress((p) => ({ ...p, [file.name]: pct }));
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              uploaded.push({ url: meta.publicUrl, type: file.type.startsWith("image") ? "image" : "video" });
              dbg('upload done', { index: i, filename: file?.name, status: xhr.status });
              resolve();
            } else {
              dbg('upload failed', { index: i, filename: file?.name, status: xhr.status });
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error("Network error during upload."));
          xhr.send(file);
        });
      }
      dbg('uploadAllMedia: all uploads completed');
      return uploaded;
    } finally {
      setUploading(false);   // always clear spinner
    }

  };

  // ───────────────────────── SUBMIT
  const submitForm = async () => {
    // prevent double submit
    if (submitting || uploading) return;
    dbg('submitForm: clicked', { submitting, uploading });
    const ok = validateBeforeSubmit();
    dbg('submitForm: validateBeforeSubmit ->', ok, { errors });
    if (!ok) return;

    setSubmitting(true);
    setErrors({});
    try {
      dbg('submitForm: start');

      // 1) Upload media to GCS (signed URLs) → get public URLs
      dbg('submitForm: uploadAllMedia start');
      const uploadedMedia = await uploadAllMedia();
      dbg('submitForm: uploadAllMedia done', uploadedMedia);

      // 2) Create the post (JSON body with uploaded asset URLs)
      const postBody = {
        business_id: businessId,
        caption,
        media: uploadedMedia,
        available_from: availableFrom, // new
      };
      dbg('submitForm: POST /ad/business/posts', postBody);
      const { data: post } = await axiosInstance.post("/ad/business/posts", postBody);
      dbg('/ad/business/posts OK', post);

      // If no boost, we're done
      if (!boostEnabled) {
        dbg('submitForm: no boost, finished');
        setSuccessMessage("Post created successfully!");
        resetForm();
        return;
      }
      if (isIOSNativeApp) {
        alert("Boosting isn’t available in the iOS app. Please use the web.");
        return;
      }
      // 3) Create the campaign (optionally save targeting preset)
      const currentPresetPayload = {
        source: 'boost',
        targeting: {
          country_codes: countryCode ? [countryCode] : [],
          city_ids: cityId ? [Number(cityId)] : null,
          min_age: apiAges.min,
          max_age: apiAges.max,
          sex: sexTarget,
        }
      };
      let saveName = (newPresetName || '').trim();
      let duplicatePreset = null;
      if (boostEnabled && saveName) {
        // Detect identical saved payload (do not block submission)
        const sNow = stableStringify(currentPresetPayload);
        duplicatePreset = presets.find((p) => {
          let pl = p?.payload;
          try { if (typeof pl === 'string') pl = JSON.parse(pl); } catch { /* ignore */ }
          return stableStringify(pl || {}) === sNow;
        }) || null;
        if (duplicatePreset) {
          setErrors((e) => ({ ...e, preset: `Identical targeting already saved as "${duplicatePreset.name}". It will not be saved again.` }));
          saveName = '';
        }
      }
      const startAtIso = (() => {
        try {
          const d = new Date(availableFrom);
          return isNaN(d.getTime()) ? availableFrom : d.toISOString();
        } catch { return availableFrom; }
      })();

      const payload = {
        businessId: businessId,
        postId: post.id,
        objective,
        durationDays: boostDuration,
        totalBudgetCents: toBaseCents(boostBudget, boostCurrency) * Math.max(1, boostDuration),
        start_at: startAtIso,
        targeting: {
          country_codes: [countryCode],
          city_ids: cityId ? [Number(cityId)] : null,
          min_age: apiAges.min,
          max_age: apiAges.max,
          sex: sexTarget
        },
        ctaType, // "reservation" | "product" | "none"
        entityId: reservationCTA
          ? (reservationServiceId || null)
          : purchaseCTA
            ? (purchaseProductId || null)
            : eventCTA
              ? (eventId || null)
              : null,
        // Allow videos with CTAs; do not require image first
        requireEntityPhotoAsFirst: false,
        saveTargetingAs: boostEnabled && saveName ? saveName : undefined,
      };

      dbg('submitForm: POST /ad/campaigns', {
        ...payload,
        _debug: {
          perDayBase: toBaseCents(boostBudget, boostCurrency),
          budgetCaps,
        }
      });
      const { data: camp } = await axiosInstance.post("/ad/campaigns", payload);
      dbg('/ad/campaigns OK', camp);
      // reflect saved preset locally
      if (camp?.presetSaved && camp?.preset) {
        setPresets((prev) => [{ id: camp.preset.id, name: camp.preset.name, payload: camp.preset.payload }, ...prev]);
        setNewPresetName('');
      } else if (camp?.presetDuplicate && saveName) {
        setErrors((e) => ({ ...e, preset: `Identical targeting already saved as "${camp.presetDuplicate.name}". It was not saved again.` }));
      }

      // 4) If payment required, go to checkout
      if (camp?.requiresPayment) {
        if (!effectiveStripeKey) {
          throw new Error('Missing Stripe publishable key');
        }
        dbg('submitForm: POST /ad/wallet/checkout-session');
        const { data: session } = await axiosInstance.post(
          "/ad/wallet/checkout-session",
          { business_id: businessId, amountDisplay: { amount: boostBudget, currency: boostCurrency } }
        );
        dbg('/ad/wallet/checkout-session OK', session);
        const stripe = await loadStripe(effectiveStripeKey);
        if (!stripe) throw new Error("Stripe failed to load");
        await stripe.redirectToCheckout({ sessionId: session.id });
        return;
      }

      setSuccessMessage("Post created and campaign activated!");
      resetForm();
    } catch (err) {
      console.error('[BusinessPostForm][submitForm] ERROR', err?.response?.status, err?.response?.data || err?.message || err);
      setErrors({ submit: err?.response?.data?.message || err?.message || "Something went wrong" });
      setUploading(false);
    } finally {
      dbg('submitForm: finally, setSubmitting(false)');
      setSubmitting(false);
    }
  };

  // Observe disabled state for the submit button
  const isSubmitDisabled = submitting || loadingConfig || uploading || (boostEnabled && belowMinDaily);
  useEffect(() => {
    dbg('Submit button disabled?', isSubmitDisabled, { submitting, loadingConfig, uploading, belowMinDaily, boostEnabled });
  }, [isSubmitDisabled, submitting, loadingConfig, uploading, belowMinDaily, boostEnabled]);

  // ───────────────────────── RESET
  const resetForm = () => {
    setCaption("");
    setMediaFiles([]);
    setReservationCTA(false);
    setPurchaseCTA(false);
    setReservationServiceId("");
    setPurchaseProductId("");
    setEventCTA(false);
    setEventId("");
    setBoostEnabled(false);
    setBoostBudget(10);
    setBoostDuration(3);
    setCountryCode("");
    setCityId("");
    setEst({
      p10: { impressions: 0, clicks: 0, conv: 0 },
      p50: { impressions: 0, clicks: 0, conv: 0 },
      p90: { impressions: 0, clicks: 0, conv: 0 }
    });
    setUploading(false);
    setUploadProgress({});
  };

  // ───────────────────────── UI
  return (
    <div className="mx-auto max-w-3xl p-4 md:p-6 lg:p-8 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold mb-4">Create Business Post</h2>

      {formError && (
        <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-300">
          {formError}
        </div>
      )}

      {/* MEDIA */}
      <section>
        <label className="block font-semibold mb-1">Media (max 3)</label>
        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:bg-gray-50"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={onDragOver}
          onDrop={handleDropped}
        >
          <p className="text-gray-600">
            Drag & drop files here, or{" "}
            <span className="text-blue-600 underline">browse</span>
          </p>
          <p className="text-sm text-gray-400 mt-1">
            JPEG, PNG, WEBP, MP4, MOV · up to 3 files
          </p>
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            ref={fileInputRef}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
        {errors.media && <p className="text-red-600 text-sm mt-1">{errors.media}</p>}

        {/* PREVIEWS + UPLOAD PROGRESS */}
        {mediaFiles.length > 0 && (
          <div className="flex gap-4 mt-4 flex-wrap">
            {mediaFiles.map((file, idx) => (
              <div key={idx} className="relative group">
                {file.type.startsWith("image") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="h-24 w-24 object-cover rounded-lg"
                  />
                ) : (
                  <video
                    src={URL.createObjectURL(file)}
                    className="h-24 w-24 object-cover rounded-lg"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeMedia(idx)}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                >
                  ✕
                </button>

                {uploading && (
                  <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                    <div className="bg-white rounded-full px-3 py-1 text-xs font-semibold">
                      {Math.min(100, uploadProgress[file.name] || 0)}%
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CAPTION */}
      <section className="mt-6">
        <label className="block font-semibold mb-1">Caption</label>
        <textarea
          className="w-full border rounded-lg p-2"
          rows="3"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        {errors.caption && <p className="text-red-600 text-sm">{errors.caption}</p>}
      </section>

      {/* CTAs */}
      <section className="mt-6 grid gap-4">
        {/* RESERVATION CTA */}
        <div className="flex items-start justify-between gap-4 p-4 border rounded-xl">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="reservationCTA"
                checked={reservationCTA}
                onChange={() => {
                  const nv = !reservationCTA;
                  setReservationCTA(nv);
                  if (nv) {
                    setPurchaseCTA(false);
                    setPurchaseProductId("");
                    setEventCTA(false);
                    setEventId("");
                  }
                }}
              />
              <label htmlFor="reservationCTA" className="font-semibold">
                Add reservation button
              </label>
            </div>

            {reservationCTA && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Link to specific service (optional)
                  </label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={reservationServiceId}
                    onChange={(e) => setReservationServiceId(e.target.value)}
                  >
                    <option value="">— None —</option>
                    {(cfg?.reservable_services || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PURCHASE CTA */}
        <div className="flex items-start justify-between gap-4 p-4 border rounded-xl">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="purchaseCTA"
                checked={purchaseCTA}
                onChange={() => {
                  const nv = !purchaseCTA;
                  setPurchaseCTA(nv);
                  if (nv) {
                    setReservationCTA(false);
                    setReservationServiceId("");
                    setEventCTA(false);
                    setEventId("");
                  }
                }}
              />
              <label htmlFor="purchaseCTA" className="font-semibold">
                Add purchase button
              </label>
            </div>

            {purchaseCTA && (
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Link to specific product (optional)
                  </label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={purchaseProductId}
                    onChange={(e) => setPurchaseProductId(e.target.value)}
                  >
                    <option value="">— None —</option>
                    {(cfg?.marketplace_products || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {errors.cta && <p className="text-red-600 text-sm">{errors.cta}</p>}
      </section>
      {/* EVENT CTA */}
      <div className="flex items-start justify-between gap-4 p-4 border rounded-xl">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="eventCTA"
              checked={eventCTA}
              onChange={() => {
                const nv = !eventCTA;
                setEventCTA(nv);
                if (nv) {
                  setReservationCTA(false);
                  setReservationServiceId("");
                  setPurchaseCTA(false);
                  setPurchaseProductId("");
                } else {
                  setEventId("");
                }
              }}
            />
            <label htmlFor="eventCTA" className="font-semibold">
              Add event button
            </label>
          </div>

          {eventCTA && (
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Link to specific event (optional)
                </label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                >
                  <option value="">— None (show upcoming) —</option>
                  {(cfg?.upcoming_events || []).map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.title} — {new Date(ev.start_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BOOST / TARGETING */}
      <section className="mt-8">
        {loadingConfig ? null : boostGloballyAvailable === false ? (
          <div className="p-4 rounded-xl border bg-gray-50 text-gray-700">
            <div className="font-semibold mb-1">Boosting isn’t available yet</div>
            <div className="text-sm">
              Advertising on our platform will soon be available. Once there are enough people to
              deliver ads responsibly, you’ll see targeting options here.
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="boostEnabled"
                checked={boostEnabled}
                onChange={() => {
                  if (isIOSNativeApp) {
                    alert("Boosting isn’t available in the iOS app. Please use the web.");
                    return;
                  }
                  setBoostEnabled((v) => !v);
                }}
                disabled={isIOSNativeApp}
              />
              <label htmlFor="boostEnabled" className="font-semibold text-lg">
                Boost this post?
              </label>
            </div>

            {boostEnabled && (
              <div className="mt-4 grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-semibold mb-1">
                    Duration: {boostDuration} day{boostDuration > 1 && "s"}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={boostDuration}
                    onChange={(e) => setBoostDuration(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                {/* Targeting Presets */}
                <div>
                  <label className="block font-semibold mb-1">Targeting Presets</label>
                  <div className="grid gap-2">
                    <div className="flex gap-2">
                      <select
                        className="flex-1 border rounded-lg p-2"
                        value={selectedPresetId}
                        onChange={(e) => setSelectedPresetId(e.target.value)}
                      >
                        <option value="">— Select preset —</option>
                        {presets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50"
                        disabled={!selectedPresetId}
                        onClick={applySelectedPreset}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 disabled:opacity-50"
                        disabled={!selectedPresetId || deletingPreset}
                        onClick={deleteSelectedPreset}
                      >
                        {deletingPreset ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                    <div className="grid gap-1">
                      <input
                        type="text"
                        className="flex-1 border rounded-lg p-2"
                        placeholder="Save current targeting as… (saved on Publish)"
                        value={newPresetName}
                        onChange={(e) => setNewPresetName(e.target.value)}
                      />
                      <div className="text-xs text-gray-500">If provided, this preset saves when you publish.</div>
                    </div>
                    {errors.preset && (
                      <p className="text-red-600 text-sm">{errors.preset}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block font-semibold mb-1">
                    Target Age Range
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      min={12}
                      max={64}
                      value={Math.max(12, Math.min(minAge, 64))}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        const [mn, mx] = clampAges(v, maxAge);
                        setMinAge(mn);
                        setMaxAge(mx);
                      }}
                      className="w-20 border rounded-lg p-2"
                    />
                    <span>to</span>
                    <input
                      type="number"
                      min={Math.max(12, Math.min(minAge, 64))}
                      max={65}
                      value={Math.max(minAge, Math.min(maxAge, 65))}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        const [mn, mx] = clampAges(minAge, v);
                        setMinAge(mn);
                        setMaxAge(mx);
                      }}
                      className="w-20 border rounded-lg p-2"
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Max of 65 is shown as <span className="font-semibold">65+</span>
                  </div>
                  {errors.ageTarget && (
                    <p className="text-amber-600 text-sm mt-1">{errors.ageTarget}</p>
                  )}
                </div>
                <label className="block font-semibold mb-1">Sex</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={sexTarget}
                  onChange={(e) => setSexTarget(e.target.value)}
                >
                  <option value="any">Any</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other / Non-binary</option>
                </select>
                <div>
                  <label className="block font-semibold mb-1">
                    Budget per day ({boostCurrency}): {boostBudget}
                  </label>
                  <input
                    type="range"
                    min="5"
                    max={budgetMaxDisplay}
                    step="5"
                    value={Math.min(boostBudget, budgetMaxDisplay)}
                    onChange={(e) => setBoostBudget(Math.min(Number(e.target.value), budgetMaxDisplay))}
                    className="w-full"
                  />
                  {errors.budget && (
                    <p className="text-red-600 text-sm mt-1">{errors.budget}</p>
                  )}
                  {liveCapNote && (
                    <p className="text-amber-600 text-sm mt-1">{liveCapNote}</p>
                  )}
                  {budgetCaps?.per_day != null && (
                    <p className="text-xs text-gray-500 mt-1">
                      Max for this audience ≈ {(budgetCaps.per_day / 100).toFixed(2)} {cfg?.base_currency}/day
                      {boostDuration ? ` · Approx total ≈ ${((budgetCaps.per_day * Math.max(1, boostDuration)) / 100).toFixed(2)} ${cfg?.base_currency} for ${boostDuration} day${boostDuration > 1 ? 's' : ''}` : ''}
                    </p>
                  )}
                  {belowMinDaily && (
                    <p className="text-red-600 text-sm mt-1">
                      Unfortunately, you cannot place an ad with these targeting settings. Try broadening age, sex, or location.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block font-semibold mb-1">Country</label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={countryCode}
                    onChange={(e) => {
                      setCountryCode(e.target.value);
                      setCityId("");
                    }}
                  >
                    <option value="">— Select —</option>
                    {(cfg?.eligible_countries || []).map((c) => (
                      <option
                        key={c.code}
                        value={c.code}
                        disabled={c.inventory === "none"}
                      >
                        {c.name}
                        {c.inventory === "low" ? " (low inventory)" : ""}
                        {c.inventory === "none" ? " (unavailable)" : ""}
                      </option>
                    ))}
                  </select>
                  {errors.countryCode && (
                    <p className="text-red-600 text-sm">{errors.countryCode}</p>
                  )}
                  {countryObj?.inventory === "low" && (
                    <p className="text-amber-600 text-xs mt-1">
                      Inventory is low here — estimates may be conservative.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block font-semibold mb-1">City (optional)</label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={cityId}
                    onChange={(e) => setCityId(e.target.value)}
                    disabled={!countryCode || !citiesForCountry.length}
                  >
                    <option value="">— Any —</option>
                    {citiesForCountry.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                  {/* p50 snapshot */}
                  {boostEnabled && (
                    <div className="mt-4 grid md:grid-cols-4 gap-4">
                      <div className="p-3 rounded-lg border">
                        <div className="text-sm text-gray-500">Reach (p50)</div>
                        <div className="text-xl font-semibold">{estimating ? '…' : (est.p50.impressions ?? 0).toLocaleString?.()}</div>
                      </div>
                      <div className="p-3 rounded-lg border">
                        <div className="text-sm text-gray-500">CTR (p50)</div>
                        <div className="text-xl font-semibold">
                          {estimating ? '…' : ((est.p50.ctr ?? 0) * 100).toFixed(2)}%
                        </div>
                      </div>
                      <div className="p-3 rounded-lg border">
                        <div className="text-sm text-gray-500">Profile visits (p50)</div>
                        <div className="text-xl font-semibold">{estimating ? '…' : (est.p50.profile_visits ?? 0).toLocaleString?.()}</div>
                      </div>
                      <div className="p-3 rounded-lg border">
                        <div className="text-sm text-gray-500">
                          {ctaType === 'none' ? 'Conversions (p50)' : 'Conversions (p50)'}
                        </div>
                        <div className="text-xl font-semibold">{estimating ? '…' : (est.p50.conv ?? 0).toLocaleString?.()}</div>
                      </div>
                    </div>
                  )}  
              </div>
            )}
          </>
        )}
      </section>
      <section className="mt-6">
        <label className="block font-semibold mb-1">Available From</label>
        <input
          type="datetime-local"
          className="w-full border rounded-lg p-2"
          value={availableFrom}
          onChange={(e) => setAvailableFrom(e.target.value)}
        />
      </section>


      {/* SUBMIT */}
      {errors.submit && (
        <p className="text-red-600 text-center mt-4">{errors.submit}</p>
      )}
      {successMessage && (
        <p className="text-green-600 text-center mt-4">{successMessage}</p>
      )}

      <button
        type="button"
        onClick={submitForm}
        disabled={submitting || loadingConfig || uploading || (boostEnabled && belowMinDaily)}
        className="mt-8 w-full md:w-auto md:px-12 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {uploading ? "Uploading…" : submitting ? "Submitting…" : (boostEnabled && belowMinDaily) ? "Targeting too narrow" : "Publish Post"}
      </button>
    </div>
  );
}
